(() => {
  const ROOT_SELECTOR = '[data-repair-request-form]';
  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_TOTAL_FILE_SIZE = 15 * 1024 * 1024;
  const SEARCH_TIMEOUT = 6000;
  const SUBMIT_MODAL_MIN_VISIBLE = 700;
  const POSTAL_LOOKUP_URL = 'https://zipcloud.ibsnet.co.jp/api/search';
  const CUSTOM_CUT_OPTION = 'カットする長さを指定する';
  const FINISH_IMAGE_OPTIONS = {
    フォーマル: ['床上 約３cm', '床上 約７cm', '足首丈', CUSTOM_CUT_OPTION],
    パンツ: ['床上 約６cm', '床上 約３cm', '床スレスレ', CUSTOM_CUT_OPTION],
    ドレス: ['床上 約３cm', '床スレスレ', '長め+３cm 余る', CUSTOM_CUT_OPTION]
  };

  function japanDateKey(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function minimumWearDateKey(now = new Date()) {
    const date = new Date(`${japanDateKey(now)}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + 14);
    return date.toISOString().slice(0, 10);
  }

  function isAllowedWearDate(value, now = new Date()) {
    if (!value) return true; // 着用予定日は任意。
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime())
      && parsed.toISOString().slice(0, 10) === value
      && value >= minimumWearDateKey(now);
  }

  async function fetchWithTimeout(url, options = {}, timeout = SEARCH_TIMEOUT) {
    const controller = new AbortController();
    const parentSignal = options.signal;
    let timedOut = false;
    const forwardAbort = () => controller.abort();

    if (parentSignal) {
      if (parentSignal.aborted) controller.abort();
      else parentSignal.addEventListener('abort', forwardAbort, { once: true });
    }

    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new Error('Search request timed out');
      throw error;
    } finally {
      window.clearTimeout(timer);
      if (parentSignal) parentSignal.removeEventListener('abort', forwardAbort);
    }
  }

  function initializeRepairForm(root) {
    if (!root || root.dataset.repairRequestInitialized === 'true') return;
    root.dataset.repairRequestInitialized = 'true';

    const form = root.querySelector('form');
    if (!form) return;

    const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
    const indicators = Array.from(root.querySelectorAll('[data-step-indicator]'));
    const typeInputs = Array.from(root.querySelectorAll('[data-repair-types] input[type="checkbox"]'));
    const typeFieldset = root.querySelector('[data-repair-types]');
    const typeSummary = root.querySelector('[data-repair-summary]');
    const typeSummaryInput = root.querySelector('[data-repair-summary-input]');
    const detailGroups = Array.from(root.querySelectorAll('[data-repair-detail]'));
    const lengthDetailGroup = root.querySelector('[data-repair-detail="着丈直し"]');
    const hemTypeInputs = Array.from(root.querySelectorAll('[data-hem-type]'));
    const hemTypePreview = root.querySelector('[data-hem-type-preview]');
    const hemTypeImage = root.querySelector('[data-hem-type-image]');
    const lengthDependentFields = Array.from(root.querySelectorAll('[data-length-dependent]'));
    const lengthDependentGroup = root.querySelector('[data-length-dependent-group]');
    const decimalNumberInputs = Array.from(root.querySelectorAll('[data-decimal-number]'));
    const finishImageSelect = root.querySelector('[data-finish-image]');
    const cutLengthGroup = root.querySelector('[data-cut-length-group]');
    const cutLengthInput = root.querySelector('[data-cut-length]');
    const sizeDetailGroup = root.querySelector('[data-repair-detail="サイズ直し"]');
    const sizeMethodInputs = Array.from(root.querySelectorAll('[data-size-method]'));
    const sizeGuidanceBlocks = Array.from(root.querySelectorAll('[data-size-guidance]'));
    const sizeDependentFields = Array.from(root.querySelectorAll('[data-size-dependent]'));
    const sizeDependentGroup = root.querySelector('[data-size-dependent-group]');
    const turnaroundField = root.querySelector('[data-turnaround-field]');
    const turnaroundInputs = Array.from(root.querySelectorAll('[data-turnaround]'));
    const turnaroundLabel = root.querySelector('[data-turnaround-label]');
    const turnaroundNormalInput = root.querySelector('[data-turnaround="normal"]');
    const turnaroundNormalDays = root.querySelector('[data-turnaround-normal-days]');
    const turnaroundExpressInput = root.querySelector('[data-turnaround="express"]');
    const confirmation = root.querySelector('[data-confirmation]');
    const submitStatus = root.querySelector('[data-submit-status]');
    const submitButton = root.querySelector('[data-submit-button]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const submitModal = root.querySelector('[data-submit-modal]');
    const submitModalDialog = root.querySelector('[data-submit-modal-dialog]');
    const submitModalSending = root.querySelector('[data-submit-modal-sending]');
    const submitModalSuccess = root.querySelector('[data-submit-modal-success]');
    const submitAgainButton = root.querySelector('[data-submit-again]');
    const endpoint = root.dataset.submissionEndpoint || '';
    let currentStep = Number(root.dataset.initialStep || 0);
    const loginReturned = setupLoginReturn(root);
    if (loginReturned) currentStep = 0;
    let confirmationUrls = [];
    let isSubmitting = false;
    let submitModalOpenedAt = 0;
    let submitModalCloseTimer;

    function showStep(step, options = {}) {
      const nextStep = Math.max(0, Math.min(step, panels.length - 1));
      currentStep = nextStep;

      panels.forEach((panel, index) => {
        const active = index === nextStep;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('is-current', index === nextStep);
        indicator.classList.toggle('is-complete', index < nextStep);
        if (index === nextStep) {
          indicator.setAttribute('aria-current', 'step');
        } else {
          indicator.removeAttribute('aria-current');
        }
      });

      if (nextStep === 2) updateTypeSummary();
      if (nextStep === 4) renderConfirmation();

      if (options.focus !== false) {
        const heading = panels[nextStep].querySelector('h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }

        const bounds = root.getBoundingClientRect();
        if (bounds.top < 0 || bounds.top > window.innerHeight * 0.65) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function clearStepError(step) {
      const panel = panels[step];
      if (!panel) return;
      const message = panel.querySelector('[data-step-error]');
      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
      panel.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    function setStepError(step, text, field) {
      const panel = panels[step];
      const message = panel && panel.querySelector('[data-step-error]');
      if (message) {
        message.textContent = text;
        message.hidden = false;
      }
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        if (typeof field.focus === 'function') field.focus();
      }
    }

    function validateStep(step) {
      clearStepError(step);
      const panel = panels[step];
      if (!panel) return true;

      const invalidField = Array.from(panel.querySelectorAll('input[required], select[required], textarea[required]'))
        .find((field) => !field.checkValidity());

      if (invalidField) {
        const message = invalidField.matches('[data-hem-type]')
          ? '裾タイプを選択してください。'
          : invalidField.matches('[data-size-method]')
            ? '脇詰めまたは脇出しを選択してください。'
          : invalidField.matches('[data-turnaround]')
            ? '仕上げ方法を選択してください。'
          : invalidField.matches('[data-cut-length]') && invalidField.validity.customError
            ? invalidField.validationMessage
          : invalidField.validity.typeMismatch
            ? '正しい形式のメールアドレスを入力してください。'
            : '必須項目を入力してください。';
        setStepError(step, message, invalidField);
        return false;
      }

      if (step === 1 && !typeInputs.some((input) => input.checked)) {
        typeFieldset.setAttribute('aria-invalid', 'true');
        setStepError(step, 'お直し項目を1つ以上選択してください。', typeInputs[0]);
        return false;
      }

      if (step === 3 && datePicker && !datePicker.validate()) {
        setStepError(step, '着用予定日は本日から2週間後（14日後）以降を選択してください。', root.querySelector('[data-date-display]'));
        return false;
      }

      return true;
    }

    root.querySelectorAll('[data-next-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        const step = Number(panel.dataset.stepPanel);
        if (validateStep(step)) showStep(step + 1);
      });
    });

    root.querySelectorAll('[data-prev-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        showStep(Number(panel.dataset.stepPanel) - 1);
      });
    });

    panels.forEach((panel, step) => {
      panel.addEventListener('input', (event) => {
        if (event.target.matches('input, select, textarea')) clearStepError(step);
      });
    });

    function selectedTypes() {
      return typeInputs.filter((input) => input.checked).map((input) => input.value);
    }

    function normalizeFullWidthNumbers(value) {
      return String(value || '')
        .replace(/[０-９]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0xfee0))
        .replace(/．/g, '.');
    }

    function sanitizeDecimalNumber(value) {
      const allowed = normalizeFullWidthNumbers(value).replace(/[^0-9.]/g, '');
      const dotIndex = allowed.indexOf('.');
      if (dotIndex < 0) return allowed;
      return `${allowed.slice(0, dotIndex + 1)}${allowed.slice(dotIndex + 1).replace(/\./g, '')}`;
    }

    function sanitizeInteger(value) {
      return normalizeFullWidthNumbers(value).replace(/[^0-9]/g, '');
    }

    function updateFinishImageOptions(hemType) {
      if (!finishImageSelect || finishImageSelect.dataset.hemType === hemType) return;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = hemType ? '選択してください' : '裾タイプを選択してください';
      finishImageSelect.replaceChildren(placeholder);

      (FINISH_IMAGE_OPTIONS[hemType] || []).forEach((label) => {
        const option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        finishImageSelect.appendChild(option);
      });

      finishImageSelect.dataset.hemType = hemType;
      finishImageSelect.value = '';
    }

    function validateCutLength() {
      if (!cutLengthInput || cutLengthInput.disabled) return;
      const value = cutLengthInput.value.trim();
      cutLengthInput.setCustomValidity(value && Number(value) < 2 ? 'カットする長さは2cm以上の整数で入力してください。' : '');
    }

    function updateCutLengthState(lengthActive, hasHemType) {
      if (!cutLengthGroup || !cutLengthInput || !finishImageSelect) return;
      const showCutLength = lengthActive && hasHemType && finishImageSelect.value === CUSTOM_CUT_OPTION;
      cutLengthGroup.hidden = !showCutLength;
      cutLengthInput.disabled = !showCutLength;
      if (!showCutLength) {
        cutLengthInput.removeAttribute('aria-invalid');
        cutLengthInput.setCustomValidity('');
      } else {
        validateCutLength();
      }
    }

    function updateLengthDependentState() {
      if (!lengthDetailGroup) return;

      const lengthActive = !lengthDetailGroup.hidden;
      const selectedHemType = hemTypeInputs.find((input) => input.checked);
      const hasHemType = Boolean(selectedHemType);
      updateFinishImageOptions(selectedHemType ? selectedHemType.value : '');
      hemTypeInputs.forEach((input) => {
        input.disabled = !lengthActive;
      });
      lengthDependentFields.forEach((field) => {
        field.disabled = !lengthActive || !hasHemType;
        if (field.disabled) field.removeAttribute('aria-invalid');
      });

      if (lengthDependentGroup) {
        const disabled = !lengthActive || !hasHemType;
        lengthDependentGroup.classList.toggle('is-disabled', disabled);
        lengthDependentGroup.setAttribute('aria-disabled', String(disabled));
      }

      if (hemTypePreview && hemTypeImage) {
        const showImage = lengthActive && selectedHemType && selectedHemType.dataset.hemImageSrc;
        hemTypePreview.hidden = !showImage;
        if (showImage) {
          hemTypeImage.src = selectedHemType.dataset.hemImageSrc;
          hemTypeImage.alt = `${selectedHemType.value}の裾タイプ参考画像`;
        } else {
          hemTypeImage.removeAttribute('src');
          hemTypeImage.alt = '';
        }
      }

      updateCutLengthState(lengthActive, hasHemType);
    }

    function updateSizeDependentState() {
      if (!sizeDetailGroup) return;

      const sizeActive = !sizeDetailGroup.hidden;
      const selectedMethod = sizeMethodInputs.find((input) => input.checked);
      const hasMethod = Boolean(selectedMethod);

      sizeMethodInputs.forEach((input) => {
        input.disabled = !sizeActive;
      });
      sizeDependentFields.forEach((field) => {
        field.disabled = !sizeActive || !hasMethod;
        if (field.disabled) field.removeAttribute('aria-invalid');
      });
      sizeGuidanceBlocks.forEach((block) => {
        block.hidden = !sizeActive || !selectedMethod || block.dataset.sizeGuidance !== selectedMethod.value;
      });

      if (sizeDependentGroup) {
        const disabled = !sizeActive || !hasMethod;
        sizeDependentGroup.classList.toggle('is-disabled', disabled);
        sizeDependentGroup.setAttribute('aria-disabled', String(disabled));
      }
    }

    function updateTurnaroundState() {
      if (!turnaroundField) return;

      const values = selectedTypes();
      const hasLengthRepair = values.includes('着丈直し');
      const hasSizeRepair = values.includes('サイズ直し');
      const active = hasLengthRepair || hasSizeRepair;
      turnaroundField.hidden = !active;
      turnaroundInputs.forEach((input) => {
        input.disabled = !active;
        if (!active) input.removeAttribute('aria-invalid');
      });

      if (turnaroundLabel) {
        turnaroundLabel.textContent = hasSizeRepair
          ? 'サイズ直し（仕上り目安）'
          : '着丈直し（仕上げ方法）';
      }

      if (turnaroundNormalInput && turnaroundNormalDays) {
        const normalDays = hasSizeRepair ? '10〜14日' : '8〜10日';
        turnaroundNormalInput.value = `通常仕上げ（${normalDays}）`;
        turnaroundNormalDays.textContent = normalDays;
      }

      if (turnaroundExpressInput) {
        turnaroundExpressInput.disabled = !active || hasSizeRepair;
        if (hasSizeRepair && turnaroundExpressInput.checked) {
          turnaroundExpressInput.checked = false;
        }
      }
    }

    function updateRepairDetailVisibility() {
      const values = selectedTypes();

      detailGroups.forEach((group) => {
        const active = values.includes(group.dataset.repairDetail);
        group.hidden = !active;
        group.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !active;
          if (!active) field.removeAttribute('aria-invalid');
        });
      });

      updateLengthDependentState();
      updateSizeDependentState();
      updateTurnaroundState();
    }

    function updateTypeSummary() {
      const values = selectedTypes();
      typeSummaryInput.value = values.join('、');
      typeSummary.replaceChildren();
      updateRepairDetailVisibility();

      if (!values.length) {
        const empty = document.createElement('span');
        empty.className = 'repair-request__hint';
        empty.textContent = '未選択';
        typeSummary.appendChild(empty);
        return;
      }

      values.forEach((value) => {
        const chip = document.createElement('span');
        chip.className = 'repair-request__type-chip';
        chip.textContent = value;
        typeSummary.appendChild(chip);
      });
    }

    typeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        typeFieldset.removeAttribute('aria-invalid');
        clearStepError(1);
        updateTypeSummary();
      });
    });

    hemTypeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updateLengthDependentState();
        clearStepError(2);
      });
    });

    sizeMethodInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updateSizeDependentState();
        clearStepError(2);
      });
    });

    decimalNumberInputs.forEach((input) => {
      input.addEventListener('input', () => {
        input.value = sanitizeDecimalNumber(input.value);
      });
    });

    if (finishImageSelect) {
      finishImageSelect.addEventListener('change', () => {
        const lengthActive = lengthDetailGroup && !lengthDetailGroup.hidden;
        updateCutLengthState(Boolean(lengthActive), hemTypeInputs.some((input) => input.checked));
        clearStepError(2);
      });
    }

    if (cutLengthInput) {
      cutLengthInput.addEventListener('input', () => {
        cutLengthInput.value = sanitizeInteger(cutLengthInput.value);
        validateCutLength();
      });
    }

    const postalCode = root.querySelector('[data-postal-code]');
    const prefecture = root.querySelector('[data-prefecture]');
    const municipality = root.querySelector('[data-municipality]');
    const postalStatus = root.querySelector('[data-postal-status]');
    let postalLookupController;
    let lastPostalLookup = '';

    function resetRestoredAddress() {
      if (!prefecture || !postalCode || !root.isConnected) return;
      const postalDigits = postalCode.value.replace(/\D/g, '');
      if (postalDigits.length < 7) {
        prefecture.value = '';
        if (municipality) municipality.value = '';
      }
    }

    resetRestoredAddress();
    window.setTimeout(resetRestoredAddress, 300);
    window.addEventListener('pageshow', resetRestoredAddress);

    async function lookupPostalAddress(postal) {
      if (!prefecture || !municipality || postal === lastPostalLookup) return;
      if (postalLookupController) postalLookupController.abort();

      const requestController = new AbortController();
      postalLookupController = requestController;
      lastPostalLookup = postal;
      postalStatus.textContent = '住所を検索中…';

      try {
        const url = new URL(POSTAL_LOOKUP_URL);
        url.searchParams.set('zipcode', postal);
        const response = await fetchWithTimeout(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: requestController.signal
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || !payload || payload.status !== 200) {
          throw new Error(payload && payload.message ? payload.message : '住所検索に失敗しました。');
        }

        const address = Array.isArray(payload.results) ? payload.results[0] : null;
        if (!address) {
          prefecture.value = '';
          municipality.value = '';
          postalStatus.textContent = '該当する住所が見つかりませんでした。';
          return;
        }

        prefecture.value = address.address1 || '';
        municipality.value = `${address.address2 || ''}${address.address3 || ''}`;
        postalStatus.textContent = '都道府県と市区町を自動入力しました。';
      } catch (error) {
        if (error.name === 'AbortError') return;
        lastPostalLookup = '';
        postalStatus.textContent = '住所を自動入力できませんでした。手動で入力してください。';
      }
    }

    if (postalCode) {
      postalCode.addEventListener('input', () => {
        const normalized = postalCode.value
          .replace(/[０-９]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0xfee0))
          .replace(/\D/g, '')
          .slice(0, 7);
        postalCode.value = normalized.length > 3
          ? `${normalized.slice(0, 3)}-${normalized.slice(3)}`
          : normalized;

        if (normalized.length === 7) {
          lookupPostalAddress(normalized);
        } else {
          if (postalLookupController) postalLookupController.abort();
          lastPostalLookup = '';
          if (postalStatus) postalStatus.textContent = '';
        }
      });
    }

    const datePicker = setupCustomDatePicker(root);

    const productController = setupProductSearch(root);
    const orderHistory = setupOrderHistory(root, productController);
    const attachmentController = setupImageAttachments(root);
    setupLoginModal(root);
    if (!loginReturned && !root.querySelector('[data-login-errors]')) setupEntryModal(root);

    function getConfirmationValue(field) {
      let value = String(field.value || '').trim();

      if (field.tagName === 'SELECT' && value) {
        const selectedOption = field.options[field.selectedIndex];
        value = selectedOption ? selectedOption.textContent.trim() : value;
      }

      if ((field.type === 'date' || field.hasAttribute('data-confirm-date')) && value) {
        const parts = value.split('-').map(Number);
        if (parts.length === 3 && parts.every(Number.isFinite)) {
          value = `${parts[0]}年${parts[1]}月${parts[2]}日`;
        }
      }

      const suffix = field.dataset.confirmSuffix || '';
      return value ? `${value}${suffix}` : '—';
    }

    function createConfirmationRow(label) {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      row.className = 'repair-request__confirmation-row';
      term.textContent = label;
      row.append(term, description);
      confirmation.appendChild(row);
      return description;
    }

    function renderConfirmation() {
      if (!confirmation) return;

      confirmationUrls.forEach((url) => URL.revokeObjectURL(url));
      confirmationUrls = [];
      confirmation.replaceChildren();

      form.querySelectorAll('[data-confirm-label]').forEach((field) => {
        if (field.disabled) return;
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        const description = createConfirmationRow(field.dataset.confirmLabel);
        description.textContent = getConfirmationValue(field);
      });

      const files = attachmentController.files();
      const imageDescription = createConfirmationRow('添付画像');

      if (!files.length) {
        imageDescription.textContent = '—';
        return;
      }

      const imageList = document.createElement('div');
      imageList.className = 'repair-request__confirmation-images';
      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const caption = document.createElement('figcaption');
        const url = URL.createObjectURL(file);

        confirmationUrls.push(url);
        figure.className = 'repair-request__confirmation-image';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        caption.textContent = file.name;
        figure.append(image, caption);
        imageList.appendChild(figure);
      });
      imageDescription.appendChild(imageList);
    }

    function validateAllSteps() {
      for (let step = 0; step < panels.length; step += 1) {
        if (!validateStep(step)) {
          showStep(step, { focus: false });
          const invalid = panels[step].querySelector('[aria-invalid="true"]');
          if (invalid && typeof invalid.focus === 'function') invalid.focus();
          return false;
        }
      }
      return true;
    }

    function setSubmitStatus(message, type) {
      submitStatus.textContent = message;
      submitStatus.className = `repair-request__notice repair-request__notice--${type}`;
      submitStatus.hidden = false;
    }

    function openSubmitModal() {
      if (!submitModal) return;
      window.clearTimeout(submitModalCloseTimer);
      if (submitModalSending) submitModalSending.hidden = false;
      if (submitModalSuccess) submitModalSuccess.hidden = true;
      submitModal.classList.remove('is-complete');
      if (submitModalDialog) {
        const sendingTitle = submitModalSending && submitModalSending.querySelector('.repair-request-submit-modal__title');
        const sendingText = submitModalSending && submitModalSending.querySelector('.repair-request-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'true');
        if (sendingTitle) submitModalDialog.setAttribute('aria-labelledby', sendingTitle.id);
        if (sendingText) submitModalDialog.setAttribute('aria-describedby', sendingText.id);
      }
      submitModalOpenedAt = performance.now();
      submitModal.hidden = false;
      submitModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('repair-request-submit-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          submitModal.classList.add('is-open');
          if (submitModalDialog) submitModalDialog.focus({ preventScroll: true });
        });
      });
    }

    function showSubmitSuccessModal() {
      if (!submitModal || !submitModalSuccess) return;
      if (submitModalSending) submitModalSending.hidden = true;
      submitModalSuccess.hidden = false;
      submitModal.classList.add('is-complete');

      if (submitModalDialog) {
        const successTitle = submitModalSuccess.querySelector('.repair-request-submit-modal__title');
        const successText = submitModalSuccess.querySelector('.repair-request-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'false');
        if (successTitle) submitModalDialog.setAttribute('aria-labelledby', successTitle.id);
        if (successText) submitModalDialog.setAttribute('aria-describedby', successText.id);
      }

      window.requestAnimationFrame(() => {
        if (submitAgainButton) submitAgainButton.focus({ preventScroll: true });
      });
    }

    async function waitForSubmitModalAnimation() {
      if (!submitModalOpenedAt) return;
      const remaining = SUBMIT_MODAL_MIN_VISIBLE - (performance.now() - submitModalOpenedAt);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
    }

    function closeSubmitModal() {
      if (!submitModal) return;
      submitModal.classList.remove('is-open');
      submitModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('repair-request-submit-modal-open');
      submitModalOpenedAt = 0;
      window.clearTimeout(submitModalCloseTimer);
      submitModalCloseTimer = window.setTimeout(() => {
        submitModal.hidden = true;
        submitModal.classList.remove('is-complete');
      }, 420);
    }

    if (submitModal) {
      submitModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(submitModal.querySelectorAll('a[href], button:not([disabled])'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    if (submitAgainButton) {
      submitAgainButton.addEventListener('click', () => {
        submitStatus.hidden = true;
        closeSubmitModal();
        showStep(0, { focus: false });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
          const firstField = panels[0] && panels[0].querySelector('input:not([type="hidden"]), select, textarea');
          if (firstField) firstField.focus({ preventScroll: true });
        }, 120);
      });
    }

    form.addEventListener('submit', async (event) => {
      if (isSubmitting) {
        event.preventDefault();
        return;
      }

      updateTypeSummary();

      if (!validateAllSteps()) {
        event.preventDefault();
        return;
      }

      if (!endpoint) return;
      event.preventDefault();

      isSubmitting = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitLabel.textContent = '送信中…';
      submitStatus.hidden = true;
      openSubmitModal();
      let submissionSucceeded = false;

      try {
        const formData = new FormData(form);
        formData.delete('images[]');
        attachmentController.files().forEach((file) => formData.append('images[]', file, file.name));

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        let responseBody = null;
        if ((response.headers.get('content-type') || '').includes('application/json')) {
          responseBody = await response.json().catch(() => null);
        }

        if (!response.ok || (responseBody && responseBody.success === false)) {
          throw new Error(responseBody && responseBody.message ? responseBody.message : '送信に失敗しました。');
        }

        form.reset();
        attachmentController.reset();
        clearSelectedProduct(root);
        orderHistory.reset();
        updateTypeSummary();
        panels.forEach((panel, step) => clearStepError(step));
        showStep(0, { focus: false });
        setSubmitStatus(responseBody && responseBody.message ? responseBody.message : root.dataset.successMessage, 'success');
        submissionSucceeded = true;
      } catch (error) {
        setSubmitStatus(error.message || '送信できませんでした。時間をおいて再度お試しください。', 'error');
      } finally {
        await waitForSubmitModalAnimation();
        if (submissionSucceeded) showSubmitSuccessModal();
        else closeSubmitModal();
        isSubmitting = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitLabel.textContent = '依頼内容送信';
        if (!submissionSucceeded) {
          submitStatus.setAttribute('tabindex', '-1');
          submitStatus.focus({ preventScroll: true });
        }
      }
    });

    updateTypeSummary();
    showStep(currentStep, { focus: false });
  }

  function setupCustomDatePicker(root) {
    const picker = root.querySelector('[data-custom-date-picker]');
    if (!picker) return;

    const displayInput = picker.querySelector('[data-date-display]');
    const valueInput = picker.querySelector('[data-wear-date]');
    const toggleButton = picker.querySelector('[data-date-toggle]');
    const calendar = picker.querySelector('[data-date-calendar]');
    const monthLabel = picker.querySelector('[data-date-month]');
    const dateGrid = picker.querySelector('[data-date-grid]');
    const previousButton = picker.querySelector('[data-date-prev]');
    const nextButton = picker.querySelector('[data-date-next]');
    const clearButton = picker.querySelector('[data-date-clear]');
    if (!displayInput || !valueInput || !toggleButton || !calendar || !monthLabel || !dateGrid) return;

    let today;
    let minimumDate;

    function refreshDateLimit() {
      today = parseDateKey(japanDateKey());
      minimumDate = parseDateKey(minimumWearDateKey());
      const hint = picker.querySelector('[data-wear-date-hint]');
      if (hint) {
        hint.textContent = `本日から2週間後（${minimumDate.getFullYear()}年${minimumDate.getMonth() + 1}月${minimumDate.getDate()}日）以降を選択できます。`;
      }
    }

    refreshDateLimit();

    function dateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    function parseDateKey(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
      const parts = String(value || '').split('-').map(Number);
      if (parts.length !== 3 || !parts.every(Number.isFinite)) return null;
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return Number.isNaN(date.getTime()) || dateKey(date) !== value ? null : date;
    }

    function sameDate(left, right) {
      return Boolean(left && right) && dateKey(left) === dateKey(right);
    }

    let selectedDate = parseDateKey(valueInput.value);
    if (selectedDate && selectedDate < minimumDate) selectedDate = null;
    let visibleMonth = new Date(
      (selectedDate || minimumDate).getFullYear(),
      (selectedDate || minimumDate).getMonth(),
      1
    );

    function updateDisplay() {
      displayInput.setCustomValidity('');
      valueInput.value = selectedDate ? dateKey(selectedDate) : '';
      displayInput.value = selectedDate
        ? `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日`
        : '';
    }

    function closeCalendar(returnFocus = false) {
      calendar.hidden = true;
      picker.classList.remove('is-open');
      displayInput.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggleButton.focus();
    }

    function renderCalendar() {
      refreshDateLimit();
      const earliestMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
      if (visibleMonth < earliestMonth) visibleMonth = earliestMonth;
      const year = visibleMonth.getFullYear();
      const month = visibleMonth.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      monthLabel.textContent = `${year}年 ${month + 1}月`;
      dateGrid.replaceChildren();

      if (previousButton) {
        previousButton.disabled = visibleMonth <= earliestMonth;
      }

      for (let index = 0; index < firstWeekday; index += 1) {
        const blank = document.createElement('span');
        blank.className = 'repair-request__calendar-blank';
        blank.setAttribute('aria-hidden', 'true');
        dateGrid.appendChild(blank);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'repair-request__calendar-day';
        button.textContent = String(day);
        button.dataset.dateValue = dateKey(date);
        button.setAttribute('aria-label', `${year}年${month + 1}月${day}日`);
        button.disabled = date < minimumDate;
        button.classList.toggle('is-today', sameDate(date, today));
        button.classList.toggle('is-selected', sameDate(date, selectedDate));
        button.setAttribute('aria-pressed', sameDate(date, selectedDate) ? 'true' : 'false');
        button.addEventListener('click', () => {
          if (!isAllowedWearDate(dateKey(date))) {
            renderCalendar();
            return;
          }
          selectedDate = date;
          updateDisplay();
          valueInput.dispatchEvent(new Event('input', { bubbles: true }));
          valueInput.dispatchEvent(new Event('change', { bubbles: true }));
          closeCalendar(true);
        });
        dateGrid.appendChild(button);
      }
    }

    function openCalendar() {
      refreshDateLimit();
      if (selectedDate && selectedDate < minimumDate) selectedDate = null;
      updateDisplay();
      calendar.hidden = false;
      picker.classList.add('is-open');
      displayInput.setAttribute('aria-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      renderCalendar();
      window.requestAnimationFrame(() => {
        const focusTarget = calendar.querySelector('.is-selected:not(:disabled), .is-today:not(:disabled), .repair-request__calendar-day:not(:disabled)');
        if (focusTarget) focusTarget.focus();
      });
    }

    function toggleCalendar() {
      if (calendar.hidden) openCalendar();
      else closeCalendar();
    }

    displayInput.addEventListener('click', toggleCalendar);
    displayInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleCalendar();
      }
    });
    toggleButton.addEventListener('click', toggleCalendar);

    if (previousButton) {
      previousButton.addEventListener('click', () => {
        if (previousButton.disabled) return;
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
        renderCalendar();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
        renderCalendar();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        selectedDate = null;
        refreshDateLimit();
        visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
        updateDisplay();
        valueInput.dispatchEvent(new Event('input', { bubbles: true }));
        valueInput.dispatchEvent(new Event('change', { bubbles: true }));
        closeCalendar(true);
      });
    }

    calendar.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCalendar(true);
      }
    });

    document.addEventListener('click', (event) => {
      if (!picker.contains(event.target)) closeCalendar();
    });

    const form = picker.closest('form');
    if (form) {
      form.addEventListener('reset', () => {
        window.requestAnimationFrame(() => {
          selectedDate = null;
          refreshDateLimit();
          visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
          updateDisplay();
          closeCalendar();
        });
      });
    }

    updateDisplay();
    renderCalendar();
    return {
      validate() {
        refreshDateLimit();
        const valid = isAllowedWearDate(valueInput.value);
        displayInput.setCustomValidity(valid ? '' : '着用予定日は本日から2週間後（14日後）以降を選択してください。');
        return valid;
      }
    };
  }

  // Only a short-lived navigation marker is stored, never credentials or order data.
  function setupLoginReturn(root) {
    const key = `repair-login-resume:${window.location.pathname}:${root.dataset.sectionId}`;
    let returned = false;
    try {
      const savedAt = Number(window.sessionStorage.getItem(key));
      const age = Date.now() - savedAt;
      returned = savedAt > 0 && age >= 0 && age < 30 * 60 * 1000;
      window.sessionStorage.removeItem(key);
    } catch (_) { /* Storage may be unavailable; native login still works. */ }
    const clear = () => {
      try { window.sessionStorage.removeItem(key); } catch (_) { /* Optional navigation marker. */ }
    };
    const loginForm = root.querySelector('[data-repair-login-form]');
    if (loginForm) loginForm.addEventListener('submit', () => {
      try { window.sessionStorage.setItem(key, String(Date.now())); } catch (_) { /* Optional navigation marker. */ }
    });
    const close = root.querySelector('[data-close-login]');
    if (close) close.addEventListener('click', clear);
    return returned;
  }

  function setupLoginModal(root) {
    const trigger = root.querySelector('[data-open-login]');
    const modal = root.querySelector('[data-login-modal]');
    if (!trigger || !modal) return;
    const dialog = modal.querySelector('[data-login-dialog]');
    const email = modal.querySelector('[data-login-email]');
    const password = modal.querySelector('[data-login-password]');
    const closeButton = modal.querySelector('[data-close-login]');
    const loginForm = modal.querySelector('[data-repair-login-form]');
    const submitButton = modal.querySelector('[data-login-submit]');
    const submitLabel = modal.querySelector('[data-login-submit-label]');
    const spinner = modal.querySelector('[data-login-spinner]');
    const formContent = root.querySelector('.repair-request__inner');
    const formEmail = root.querySelector('input[name="contact[email]"]');
    let closeTimer;
    let previouslyInert = false;
    let loadingTimer;

    function resetLoading() {
      window.clearTimeout(loadingTimer);
      if (submitButton) submitButton.setAttribute('aria-busy', 'false');
      if (submitLabel) submitLabel.textContent = 'ログイン';
      if (spinner) spinner.hidden = true;
    }

    function showLoading() {
      resetLoading();
      if (submitButton) submitButton.setAttribute('aria-busy', 'true');
      if (submitLabel) submitLabel.textContent = 'ログイン中…';
      if (spinner) spinner.hidden = false;
      // CAPTCHA or a cancelled navigation must never leave a permanent loading state.
      loadingTimer = window.setTimeout(resetLoading, 30000);
    }

    function open() {
      window.clearTimeout(closeTimer);
      resetLoading();
      if (modal.hidden) previouslyInert = Boolean(formContent && formContent.inert);
      modal.hidden = false;
      if (formContent) formContent.inert = true;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('repair-request-login-modal-open');
      if (!email.value && formEmail) email.value = formEmail.value;
      window.requestAnimationFrame(() => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        modal.classList.add('is-open');
        email.focus({ preventScroll: true });
      });
    }

    function close() {
      resetLoading();
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      // Do not retain a password after dismissing the dialog.
      password.value = '';
      if (formContent) formContent.inert = previouslyInert;
      document.body.classList.remove('repair-request-login-modal-open');
      trigger.focus({ preventScroll: true });
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => { modal.hidden = true; }, 200);
    }

    trigger.addEventListener('click', (event) => {
      // Preserve the real login URL for new tabs and browsers without this script.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open();
    });
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep normal tab navigation in the dialog; do not trap external CAPTCHA challenges.
      const focusable = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), iframe'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    });
    // Visual feedback only: do not cancel native POST, disable credentials/the
    // submitter, or interfere with Shopify's CAPTCHA submission/retry handlers.
    if (loginForm) {
      loginForm.addEventListener('submit', showLoading, true);
      loginForm.addEventListener('invalid', resetLoading, true);
      loginForm.addEventListener('input', resetLoading);
      loginForm.addEventListener('reset', resetLoading);
    }
    window.addEventListener('pageshow', resetLoading);
    resetLoading();
    if (modal.querySelector('[data-login-errors]')) open();
  }

  function setupOrderHistory(root, productController) {
    const history = root.querySelector('[data-order-history]');
    if (!history || !productController) return { reset() {} };
    const number = root.querySelector('[data-order-number]');
    const ordersSelect = history.querySelector('[data-order-select]');
    const productField = root.querySelector('[data-order-product-field]');
    const productsSelect = root.querySelector('[data-order-product-select]');
    const manualField = root.querySelector('[data-manual-product-field]');
    const more = history.querySelector('[data-orders-more]');
    const status = history.querySelector('[data-orders-status]');
    const orders = new Map();
    let nextUrl = '';
    let pendingRequest;

    function option(value, label) {
      const node = document.createElement('option');
      node.value = value;
      node.textContent = label;
      return node;
    }

    function syncProductMode() {
      const hasOrder = orders.has(ordersSelect.value);
      manualField.hidden = hasOrder;
      productField.hidden = !hasOrder;
      productsSelect.disabled = !hasOrder || !orders.get(ordersSelect.value).length;
      productController.setManualMode(!hasOrder);
    }

    function clearProducts() {
      productController.reset();
      productsSelect.replaceChildren(option('', '商品を選択してください'));
    }

    function readOrders(source) {
      source.querySelectorAll('[data-history-order]').forEach((node) => {
        const name = node.dataset.number;
        if (!name || orders.has(name)) return;
        const products = [];
        const seen = new Set();
        node.querySelectorAll('[data-history-product]').forEach((item) => {
          const title = item.dataset.title;
          if (!title || seen.has(title)) return;
          seen.add(title);
          products.push({ title, image: item.dataset.image || '' });
        });
        orders.set(name, products);
        ordersSelect.appendChild(option(name, node.dataset.label || name));
      });
      nextUrl = source.dataset.nextUrl || '';
      more.hidden = !nextUrl;
      status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
    }

    const initialSource = history.querySelector('[data-order-history-source]');
    if (initialSource) {
      readOrders(initialSource);
      initialSource.remove();
    }
    syncProductMode();

    ordersSelect.addEventListener('change', () => {
      clearProducts();
      number.value = orders.has(ordersSelect.value) ? ordersSelect.value : '';
      const products = orders.get(ordersSelect.value) || [];
      products.forEach((product, index) => productsSelect.appendChild(option(String(index), product.title)));
      if (number.value && !products.length) {
        productsSelect.replaceChildren(option('', 'この注文に商品情報がありません'));
      }
      syncProductMode();
    });

    productsSelect.addEventListener('change', () => {
      if (productsSelect.disabled) return;
      const products = orders.get(ordersSelect.value) || [];
      const product = productsSelect.value === '' ? null : products[Number(productsSelect.value)];
      if (product) productController.select(product, 'order');
      else productController.reset();
    });

    productController.onChange((source) => {
      if (source !== 'order') productsSelect.value = '';
    });

    more.addEventListener('click', async () => {
      if (!nextUrl || pendingRequest) return;
      const controller = new AbortController();
      pendingRequest = controller;
      more.disabled = true;
      status.textContent = '注文履歴を読み込み中…';
      try {
        const url = new URL(nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          throw new Error('Invalid order history URL');
        }
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetchWithTimeout(url.toString(), {
          credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'text/html' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Order history unavailable');
        const html = await response.text();
        if (controller.signal.aborted || pendingRequest !== controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-order-history-source]');
        if (!source) throw new Error('Order history session unavailable');
        const previousSize = orders.size;
        readOrders(source);
        status.textContent = `${orders.size - previousSize}件の注文を追加しました。`;
      } catch (error) {
        if (controller.signal.aborted) return;
        status.textContent = '注文履歴を読み込めませんでした。ログイン状態を確認して再度お試しください。注文を選択せずにお進みいただくこともできます。';
      } finally {
        if (pendingRequest === controller) {
          pendingRequest = null;
          more.disabled = false;
        }
      }
    });

    return {
      reset() {
        if (pendingRequest) pendingRequest.abort();
        pendingRequest = null;
        more.disabled = false;
        ordersSelect.value = '';
        number.value = '';
        clearProducts();
        syncProductMode();
        status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
      }
    };
  }

  function setupProductSearch(root) {
    const search = root.querySelector('[data-product-search]');
    if (!search) return;

    const input = search.querySelector('[data-product-query]');
    const results = search.querySelector('[data-product-results]');
    const status = search.querySelector('[data-search-status]');
    const selected = search.querySelector('[data-selected-product]');
    const selectedName = search.querySelector('[data-selected-product-name]');
    const selectedImage = search.querySelector('[data-selected-product-image]');
    const clearButton = search.querySelector('[data-clear-product]');
    const searchButton = search.querySelector('[data-product-search-button]');
    const titleField = search.querySelector('[data-product-title]');
    const searchUrl = root.dataset.searchUrl || '/search';
    let debounceTimer;
    let abortController;
    let products = [];
    let activeIndex = -1;
    let selectedTitle = titleField.value;
    let manualMode = true;
    const selectionListeners = [];

    function cancelSearch() {
      window.clearTimeout(debounceTimer);
      if (abortController) abortController.abort();
      abortController = null;
      searchButton.disabled = !manualMode;
      products = [];
      results.replaceChildren();
      closeResults();
    }

    if (selectedImage) selectedImage.addEventListener('error', () => {
      selectedImage.hidden = true;
      selectedImage.removeAttribute('src');
    });

    function closeResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function setActiveResult(index) {
      const options = Array.from(results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        option.classList.toggle('is-focused', optionIndex === activeIndex);
        option.setAttribute('aria-selected', optionIndex === activeIndex ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function chooseProduct(product, source = 'search') {
      if (source === 'search' && !manualMode) return;
      cancelSearch();
      selectedTitle = product.title;
      input.value = product.title;
      titleField.value = product.title;
      selectedName.textContent = `選択中：${product.title}`;
      selected.hidden = false;
      if (selectedImage) {
        const image = product.featured_image && product.featured_image.url || product.image;
        selectedImage.hidden = !image;
        if (image) selectedImage.src = image;
        else selectedImage.removeAttribute('src');
      }
      status.textContent = '';
      closeResults();
      selectionListeners.forEach((listener) => listener(source));
    }

    function renderProducts(items) {
      products = items;
      results.replaceChildren();

      if (!items.length) {
        closeResults();
        status.textContent = '該当する商品が見つかりませんでした。';
        return;
      }

      items.forEach((product, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const copy = document.createElement('span');
        const title = document.createElement('strong');

        item.setAttribute('role', 'none');
        button.id = `${results.id}-option-${index}`;
        button.className = 'repair-request__result-button';
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');

        if (product.featured_image && product.featured_image.url) {
          const image = document.createElement('img');
          image.className = 'repair-request__result-image';
          image.src = product.featured_image.url;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else if (product.image) {
          const image = document.createElement('img');
          image.className = 'repair-request__result-image';
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'repair-request__result-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = '—';
          button.appendChild(placeholder);
        }

        copy.className = 'repair-request__result-copy';
        title.textContent = product.title;
        copy.appendChild(title);
        button.appendChild(copy);
        button.addEventListener('click', () => chooseProduct(product));
        item.appendChild(button);
        results.appendChild(item);
      });

      status.textContent = `${items.length}件の商品候補があります。`;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    async function fetchPredictiveProducts(query, signal) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,variants.barcode'
      });
      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const response = await fetchWithTimeout(`${routesRoot}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const payload = await response.json();
      return payload.resources.results.products || [];
    }

    async function fetchGraphqlProducts(query, signal) {
      const graphqlQuery = `
        query RepairProductSearch($query: String!) {
          search(
            first: 8
            query: $query
            types: [PRODUCT]
            prefix: LAST
            unavailableProducts: SHOW
          ) {
            nodes {
              __typename
              ... on Product {
                id
                title
                handle
                vendor
                onlineStoreUrl
                featuredImage { url }
                variants(first: 20) {
                  nodes { id title sku barcode }
                }
              }
            }
          }
        }
      `;
      const response = await fetchWithTimeout(`${window.location.origin}/api/2026-07/graphql.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: graphqlQuery, variables: { query } }),
        signal
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || payload.errors || !payload.data || !payload.data.search) {
        const message = payload && payload.errors && payload.errors[0] && payload.errors[0].message;
        throw new Error(message || `Storefront API failed: ${response.status}`);
      }

      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      return payload.data.search.nodes
        .filter((product) => product && product.__typename === 'Product')
        .map((product) => {
          const productId = String(product.id || '').split('/').pop();
          const productUrl = product.onlineStoreUrl || `${routesRoot}products/${product.handle}`;
          const variants = product.variants && Array.isArray(product.variants.nodes)
            ? product.variants.nodes.map((variant) => {
              const variantId = String(variant.id || '').split('/').pop();
              const separator = productUrl.includes('?') ? '&' : '?';
              return {
                id: variantId,
                title: variant.title,
                sku: variant.sku,
                barcode: variant.barcode,
                url: variantId ? `${productUrl}${separator}variant=${variantId}` : productUrl
              };
            })
            : [];

          return {
            id: productId,
            title: product.title,
            vendor: product.vendor,
            url: productUrl,
            image: product.featuredImage ? product.featuredImage.url : null,
            variants
          };
        });
    }

    async function fetchStorefrontProducts(query, signal) {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('view', 'repair-products');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'show');

      const response = await fetchWithTimeout(url.toString(), {
        headers: { Accept: 'application/json' },
        signal
      });
      if (!response.ok) throw new Error(`Storefront search failed: ${response.status}`);
      const payload = JSON.parse(await response.text());
      if (!payload || !Array.isArray(payload.products)) throw new Error('Invalid storefront search response');
      return payload.products;
    }

    async function fetchProducts(query) {
      if (!manualMode) return;
      if (abortController) abortController.abort();
      const requestController = new AbortController();
      abortController = requestController;
      closeResults();
      status.textContent = '検索中…';
      searchButton.disabled = true;

      try {
        let items;
        try {
          items = await fetchGraphqlProducts(query, requestController.signal);
        } catch (graphqlError) {
          if (graphqlError.name === 'AbortError') throw graphqlError;
          try {
            items = await fetchStorefrontProducts(query, requestController.signal);
          } catch (storefrontError) {
            if (storefrontError.name === 'AbortError') throw storefrontError;
            items = await fetchPredictiveProducts(query, requestController.signal);
          }
        }
        if (requestController.signal.aborted || abortController !== requestController || !manualMode) return;
        renderProducts(items);
      } catch (error) {
        if (requestController.signal.aborted || abortController !== requestController || !manualMode || error.name === 'AbortError') return;
        closeResults();
        status.textContent = '商品検索に失敗しました。時間をおいて再度検索してください。';
      } finally {
        if (abortController === requestController) {
          abortController = null;
          searchButton.disabled = !manualMode;
        }
      }
    }

    function searchCurrentValue() {
      if (!manualMode) return;
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (!query) {
        status.textContent = '品番・SKU・バーコード・商品名を入力してください。';
        input.focus();
        return;
      }
      fetchProducts(query);
    }

    input.addEventListener('input', () => {
      if (!manualMode) return;
      cancelSearch();
      if (selectedTitle && input.value !== selectedTitle) clearSelectedProduct(root, { keepQuery: true });
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) {
        if (abortController) abortController.abort();
        products = [];
        results.replaceChildren();
        closeResults();
        status.textContent = '';
        return;
      }
      debounceTimer = window.setTimeout(() => fetchProducts(query), 280);
    });

    searchButton.addEventListener('click', searchCurrentValue);

    input.addEventListener('keydown', (event) => {
      if (results.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp') && products.length) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveResult(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveResult(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && !results.hidden) {
        event.preventDefault();
        chooseProduct(products[activeIndex]);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        searchCurrentValue();
      } else if (event.key === 'Escape') {
        closeResults();
      }
    });

    clearButton.addEventListener('click', () => {
      clearSelectedProduct(root);
      selectedTitle = '';
      const focusTarget = manualMode ? input : root.querySelector('[data-order-product-select]');
      if (focusTarget) focusTarget.focus();
    });

    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) closeResults();
    });

    search._clearProductState = () => {
      cancelSearch();
      selectedTitle = '';
      products = [];
      status.textContent = '';
      closeResults();
      selectionListeners.forEach((listener) => listener('clear'));
    };
    return {
      reset: () => clearSelectedProduct(root),
      select: chooseProduct,
      onChange: (listener) => selectionListeners.push(listener),
      setManualMode(manual) {
        if (manualMode !== manual) cancelSearch();
        manualMode = manual;
        input.disabled = !manual;
        searchButton.disabled = !manual || Boolean(abortController);
      }
    };
  }

  function clearSelectedProduct(root, options = {}) {
    const search = root.querySelector('[data-product-search]');
    if (!search) return;
    const input = search.querySelector('[data-product-query]');
    if (!options.keepQuery) input.value = '';
    search.querySelector('[data-product-title]').value = '';
    search.querySelector('[data-selected-product]').hidden = true;
    const image = search.querySelector('[data-selected-product-image]');
    if (image) { image.hidden = true; image.removeAttribute('src'); }
    search.querySelector('[data-selected-product-name]').textContent = '';
    if (typeof search._clearProductState === 'function') search._clearProductState();
  }

  function setupImageAttachments(root) {
    const input = root.querySelector('[data-image-input]');
    const previews = root.querySelector('[data-image-previews]');
    const errorOutput = root.querySelector('[data-file-error]');
    let files = [];
    let previewUrls = [];

    function showError(message) {
      errorOutput.textContent = message;
      errorOutput.hidden = !message;
    }

    function syncInputFiles() {
      if (typeof DataTransfer === 'undefined') return;
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }

    function renderPreviews() {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
      previews.replaceChildren();

      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const remove = document.createElement('button');
        const url = URL.createObjectURL(file);

        previewUrls.push(url);
        figure.className = 'repair-request__preview';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${file.name}を削除`);
        remove.addEventListener('click', () => {
          files.splice(index, 1);
          syncInputFiles();
          renderPreviews();
          showError('');
        });

        figure.append(image, remove);
        previews.appendChild(figure);
      });
    }

    input.addEventListener('change', () => {
      const incoming = Array.from(input.files || []);
      const known = new Set(files.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const messages = [];

      incoming.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) return;
        if (!file.type.startsWith('image/')) {
          messages.push(`${file.name}は画像ファイルではありません。`);
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          messages.push(`${file.name}は10MBを超えています。`);
          return;
        }
        const totalSize = files.reduce((sum, currentFile) => sum + currentFile.size, 0);
        if (totalSize + file.size > MAX_TOTAL_FILE_SIZE) {
          messages.push('画像の合計容量は15MB以内にしてください。');
          return;
        }
        if (files.length >= MAX_FILES) {
          messages.push(`画像は最大${MAX_FILES}枚までです。`);
          return;
        }
        known.add(key);
        files.push(file);
      });

      syncInputFiles();
      renderPreviews();
      showError(messages.join(' '));
    });

    return {
      files: () => files.slice(),
      reset: () => {
        files = [];
        input.value = '';
        renderPreviews();
        showError('');
      }
    };
  }

  function setupEntryModal(root) {
    const modal = root.querySelector('[data-entry-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('[role="dialog"]');
    const primaryAction = modal.querySelector('.repair-request-modal__button--primary');
    const closeButtons = modal.querySelectorAll('[data-modal-close]');
    let previouslyFocused = null;
    let closeTimer;

    function focusableElements() {
      return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden);
    }

    function openModal() {
      previouslyFocused = document.activeElement;
      window.clearTimeout(closeTimer);
      modal.hidden = false;
      document.body.classList.add('repair-request-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          modal.classList.add('is-open');
          if (primaryAction) primaryAction.focus({ preventScroll: true });
        });
      });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      document.body.classList.remove('repair-request-modal-open');
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
      }, 440);

      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    }

    closeButtons.forEach((button) => button.addEventListener('click', closeModal));

    modal.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.setTimeout(openModal, 260);
  }

  function initializeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initializeRepairForm(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeRepairForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
